import React, { useCallback, useState } from 'react';
import cn from 'classnames';
import { SelectOption } from '@creditflowresearch/uilib/Select';
import { AsyncSelect } from '@framework/ui/Organisms/FinalForm/Fields/Select';
import { InputActionMeta } from 'react-select';
import { Field, useField } from '@framework/ui/Organisms/FinalForm';
import { IFinalFormFieldInputComponentProps } from '@framework/ui/Organisms/FinalForm/interfaces';
import {
  PORTFOLIO_REVIEW_UNKNOWNS_FIELD_ARRAY,
  UNKNOWN_BOND_OPTION,
  UNKNOWN_BOND_VALUE,
} from '../constants';
import { usePortfoliosContext } from '@/creditflowresearch/application/PortfolioMonitor/PortfoliosContext';
import {
  TSetPayload,
  useFormPayloadContext,
} from '@/creditflowresearch/application/PortfolioMonitor/FormPayloadContext';
import useGetPositions from '@/creditflowresearch/ui/components/portfolioMonitor/PortfolioContent/common/useGetPositions';
import {
  mapBondsToOptions,
} from '@/creditflowresearch/ui/components/portfolioMonitor/PortfolioContent/PortfolioForm/FormSteps/heplers';
import {
  ICreatePortfolioFormState,
  IPortfolioBondLocal,
  IPosition,
} from '@/creditflowresearch/domain/vo/PortfolioMonitor/ICreatePortfolioFormState';
import { getSelectInputValue } from './helpers';

import spaces from '@creditflowresearch/uilib/styles/helpers/spaces.module.scss';
import styles from './styles.module.scss';

type TInput = IFinalFormFieldInputComponentProps<{ label: string, value: IPortfolioBondLocal, customBondName?: string }>['input'];

interface IProps {
  cellName: string,
  row: IPosition,
  rowIndex: number,
}

const DealNameSelect = ({ cellName, row, rowIndex }: IProps) => {
  const { listIndex } = useGetPositions();
  const { getMappedBondsOptions, setUnknownBond } = usePortfoliosContext();
  const { payload, setPayload } = useFormPayloadContext();
  const { input } = useField(`${ PORTFOLIO_REVIEW_UNKNOWNS_FIELD_ARRAY }[${ rowIndex }].${ cellName }`);
  const [isControlShouldRenderValue, setIsControlShouldRenderValue] = useState(true);

  const loadOptions = useCallback((query: string, callback) => {
    getMappedBondsOptions(query).then((bonds) => {
      callback([...mapBondsToOptions(bonds), UNKNOWN_BOND_OPTION]);
    });
  }, [getMappedBondsOptions]);

  const updateBond = (value) => {
    setPayload((prevState) => ({
      ...prevState,
      lists: prevState.lists.map((list, idx) => {
        if (idx === listIndex) {
          return {
            ...list,
            positions: list.positions.map((pos) => (
                pos.localId === row.localId ? {
                  ...pos,
                  bond: {
                    ...value.value,
                    label: value.label,
                    customBondName: value.value?.dealId ? null : value.label,
                  },
                } : pos
            )),
          };
        }
        return list;
      }),
    }));
  };

  // @ts-ignore
  const handleInputBlurOrMenuClose = (value, input: TInput, metaData: InputActionMeta) => {
    if (metaData.action === 'input-blur' || metaData.action === 'menu-close') {
      onChange({
        value: input?.value.customBondName && !metaData.prevInputValue
            ? input?.value.customBondName : (metaData.prevInputValue || value || input?.value?.value),
        label: input?.value.customBondName && !metaData.prevInputValue
            ? input?.value.customBondName : (metaData.prevInputValue || value || input?.value?.value),
      });
    }
  };

  // @ts-ignore
  const handleInputChange = (
    value: string,
    metaData: InputActionMeta,
    listIndex: number,
    row,
    setPayload: TSetPayload,
  ) => {
    if (metaData.action === 'input-change') {
      setPayload((prevState) => {
        const indexofPosition = prevState.lists[listIndex].positions.findIndex((pos) => pos.localId === row.localId);
        if (indexofPosition >= 0) {
          let position = prevState.lists[listIndex].positions[indexofPosition];

          prevState.lists[listIndex].positions[indexofPosition] = {
            ...position,
            bond: {
              ...row.bond,
              dealId: null,
              trancheId: null,
              customBondName: value || metaData.prevInputValue,
            },
          };
        }

        return prevState;
      });
    }
  };

  const onChange = (value) => {
    if (value?.value === UNKNOWN_BOND_VALUE) {
        setUnknownBond(row);
        return;
      }

      if (value) {
        updateBond(value);
      }
  };

  const onInputChange = (
      value: string,
      // @ts-ignore
      metaData: InputActionMeta,
      input: TInput,
      listIndex: number,
      row,
      setPayload: TSetPayload,
  ) => {
    handleInputBlurOrMenuClose(value, input, metaData);
    handleInputChange(value, metaData, listIndex, row, setPayload);
  };

  const onInputChangeWrapper = useCallback(
    // @ts-ignore
    (value: string, metaData: InputActionMeta) => (
        onInputChange(value, metaData, input, listIndex, row, setPayload)
    ),
    [input, listIndex, row, setPayload, updateBond],
  );

  const CustomOption = (props) => (
    <SelectOption ownProps={ props } dataTest="option" />
  );

  const OptionWrapper = (props) => (
    <CustomOption { ...props }>
      {
        props.data.value === UNKNOWN_BOND_VALUE ? (
          <div style={ { color: '#4a76b6' } }>
            { props.children }
          </div>
        )
        :
        props.children
      }
    </CustomOption>
  );

  const onBlur = (input: TInput, payload: ICreatePortfolioFormState) => {
    const selectedOptionLabel = payload.lists[listIndex].positions
      .find((pos) => pos.localId === row.localId)?.bond?.customBondName;

    if (!selectedOptionLabel) {
      setIsControlShouldRenderValue(false);
    } else {
      setIsControlShouldRenderValue(true);
      input.onBlur();
    }
  };

  return (
    <Field
      name={ `${ PORTFOLIO_REVIEW_UNKNOWNS_FIELD_ARRAY }[${ rowIndex }].${ cellName }` }
      dataTest={ `${ PORTFOLIO_REVIEW_UNKNOWNS_FIELD_ARRAY }[${ rowIndex }].${ cellName }` }
      component={ (fieldProps) => (
        // @ts-ignore
        <AsyncSelect
          { ...fieldProps }
          menuPlacement="auto"
          defaultOptions={ [UNKNOWN_BOND_OPTION] }
          loadOptions={ loadOptions }
          components={ { Option: OptionWrapper } }
          onInputChange={ onInputChangeWrapper }
          filterOption={ () => true }
          placeholder="Type a Bond name"
          isSupportHighlighting={ false }
          formFieldClassName={ cn(spaces.mbn, styles.select) }
          // @ts-ignore
          closeMenuOnSelect={ false }
          isClearable
          input={ {
            ...fieldProps.input,
            value: getSelectInputValue(fieldProps.input),
            onBlur: () => onBlur(fieldProps.input, payload),
            onChange: (value) => {
              if (value?.value !== UNKNOWN_BOND_VALUE) {
                fieldProps.input.onChange(value);
              }
              onChange(value);
            },
          } }
          controlShouldRenderValue={ isControlShouldRenderValue }
          useDebounce
        />
      ) }
    />
  );
};

export default DealNameSelect;
